import { useState } from "react";
import AdminResource from "./AdminResource";
import AdminMediaUpload from "./AdminMediaUpload";
import AdminPartnerGallery from "./AdminPartnerGallery";
import { t } from "../../lib/i18n";

const statusLabel = (v) => (v ? t(`statuses.${v}`, v) : "—");

const PROFILE_TYPES = [
  "member", "ambassador", "creator", "influencer", "model", "talent",
  "photographer", "videographer", "filmmaker", "event_personality", "production_partner",
];

export const AdminPartners = () => {
  const [galleryPartner, setGalleryPartner] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  return (
    <>
      <AdminResource
        key={reloadKey}
        resource="partners"
        title="Partenaires"
        description="Un profil partenaire doit transmettre l'atmosphère et l'expérience vécue, pas seulement une adresse. La galerie se compose à partir des médias déjà téléversés."
        rowAction={{
          label: "Galerie",
          testid: "partner-gallery-open",
          onClick: (partner) => setGalleryPartner(partner),
        }}
        columns={[
          { key: "business_name", label: "Entreprise" },
          { key: "category", label: "Catégorie" },
          { key: "city", label: "Ville" },
          { key: "gallery_media_ids", label: "Galerie" },
          { key: "published", label: "Publié" },
          { key: "featured", label: "Vedette" },
          { key: "is_demo", label: "Démo" },
        ]}
        fields={[
          { key: "business_name", label: "Nom de l'entreprise *", type: "text" },
          { key: "slug", label: "Slug", type: "text" },
          { key: "category", label: "Catégorie", type: "text" },
          { key: "city", label: "Ville", type: "text" },
          { key: "location", label: "Quartier / adresse", type: "text" },
          { key: "logo_url", label: "Logo (URL)", type: "text" },
          { key: "hero_media_url", label: "Média hero (URL)", type: "text" },
          { key: "short_description", label: "Description courte", type: "textarea" },
          { key: "atmosphere_description", label: "Atmosphère", type: "textarea" },
          { key: "gallery", label: "Galerie héritée (URLs)", type: "csv" },
          { key: "videos", label: "Vidéos (URLs)", type: "csv" },
          { key: "website", label: "Site web", type: "text" },
          { key: "cta_label", label: "Libellé CTA", type: "text" },
          { key: "cta_url", label: "URL CTA", type: "text" },
          { key: "published", label: "Publié", type: "bool" },
          { key: "featured", label: "En vedette", type: "bool" },
          { key: "is_demo", label: "Contenu démo", type: "bool" },
        ]}
      />
      {galleryPartner && (
        <AdminPartnerGallery
          partner={galleryPartner}
          onClose={() => setGalleryPartner(null)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      )}
    </>
  );
};

export const AdminEvents = () => (
  <AdminResource
    resource="events"
    title="Événements"
    description="Types d'événements pilotés par les données : VytaliDay, VytaliNight, Partner Launch, Photoshoot, Video Shoot, Community Activation."
    columns={[
      { key: "name", label: "Nom" },
      { key: "event_type", label: "Type" },
      { key: "date_time", label: "Date" },
      { key: "published", label: "Publié" },
      { key: "participation_open", label: "Participation" },
      { key: "is_demo", label: "Démo" },
    ]}
    fields={[
      { key: "name", label: "Nom *", type: "text" },
      { key: "slug", label: "Slug", type: "text" },
      { key: "event_type", label: "Type d'événement", type: "text" },
      { key: "date_time", label: "Date et heure", type: "datetime" },
      { key: "venue", label: "Lieu", type: "text" },
      { key: "location", label: "Ville / localisation", type: "text" },
      { key: "partner_id", label: "ID partenaire", type: "text" },
      { key: "hero_media_url", label: "Média hero (URL)", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "gallery", label: "Galerie (URLs)", type: "csv" },
      { key: "creator_ids", label: "IDs créateurs", type: "csv" },
      { key: "ambassador_ids", label: "IDs ambassadeurs", type: "csv" },
      { key: "collaborator_ids", label: "IDs collaborateurs", type: "csv" },
      { key: "participant_ids", label: "IDs participants", type: "csv" },
      { key: "challenge_id", label: "ID challenge", type: "text" },
      { key: "capacity", label: "Capacité", type: "number" },
      { key: "published", label: "Publié", type: "bool" },
      { key: "participation_open", label: "Participation ouverte", type: "bool" },
      { key: "featured", label: "En vedette", type: "bool" },
      { key: "is_demo", label: "Contenu démo", type: "bool" },
    ]}
  />
);

export const AdminProfiles = () => (
  <AdminResource
    resource="profiles"
    title="Profils / Créateurs"
    description="Les types de profil sont des identités cumulables. Ils n'accordent aucun privilège système."
    columns={[
      { key: "display_name", label: "Nom" },
      { key: "city", label: "Ville" },
      { key: "profile_types", label: "Types" },
      { key: "published", label: "Publié" },
      { key: "featured", label: "Vedette" },
      { key: "is_demo", label: "Démo" },
    ]}
    fields={[
      { key: "display_name", label: "Nom affiché *", type: "text" },
      { key: "slug", label: "Slug", type: "text" },
      { key: "city", label: "Ville", type: "text" },
      { key: "profile_photo_url", label: "Photo (URL)", type: "text" },
      { key: "profile_types", label: `Types (${PROFILE_TYPES.join(", ")})`, type: "csv" },
      { key: "bio", label: "Bio", type: "textarea" },
      { key: "specialties", label: "Spécialités", type: "csv" },
      { key: "portfolio_url", label: "Portfolio", type: "text" },
      { key: "collaboration_availability", label: "Disponibilité collaboration", type: "text" },
      { key: "creator_status", label: "Statut créateur (candidate/certified)", type: "select", options: ["", "candidate", "certified", "suspended"] },
      { key: "published", label: "Publié", type: "bool" },
      { key: "featured", label: "En vedette", type: "bool" },
      { key: "is_demo", label: "Contenu démo", type: "bool" },
    ]}
  />
);

export const AdminChallenges = () => (
  <AdminResource
    resource="challenges"
    title="Challenges"
    columns={[
      { key: "title", label: "Titre" },
      { key: "status", label: "Statut", format: statusLabel },
      { key: "start_date", label: "Début" },
      { key: "end_date", label: "Fin" },
      { key: "published", label: "Publié" },
      { key: "is_demo", label: "Démo" },
    ]}
    fields={[
      { key: "title", label: "Titre *", type: "text" },
      { key: "slug", label: "Slug", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "rules", label: "Règles", type: "textarea" },
      { key: "start_date", label: "Date de début", type: "date" },
      { key: "end_date", label: "Date de fin", type: "date" },
      { key: "event_id", label: "ID événement éligible", type: "text" },
      { key: "hero_media_url", label: "Média hero (URL)", type: "text" },
      { key: "status", label: "Statut", type: "select", options: ["open", "closed", "draft"] },
      { key: "published", label: "Publié", type: "bool" },
      { key: "featured", label: "En vedette", type: "bool" },
      { key: "is_demo", label: "Contenu démo", type: "bool" },
    ]}
  />
);

export const AdminMedia = () => {
  const [reloadKey, setReloadKey] = useState(0);
  return (
    <div>
      <AdminMediaUpload onUploaded={() => setReloadKey((k) => k + 1)} />
      <AdminResource
        key={reloadKey}
        resource="media"
        title="Médias"
        description="MongoDB ne stocke que des métadonnées et des références. Aucun binaire n'est conservé en base. Un média téléversé reste en brouillon jusqu'à publication explicite."
        columns={[
          { key: "type", label: "Type" },
          { key: "caption", label: "Légende" },
          { key: "storage_provider", label: "Source" },
          { key: "publication_status", label: "Publication" },
          { key: "consent_status", label: "Consentement" },
          { key: "featured_status", label: "Vedette" },
          { key: "is_demo", label: "Démo" },
        ]}
        fields={[
          { key: "type", label: "Type", type: "select", options: ["photo", "video"] },
          { key: "url", label: "URL du média", type: "text" },
          { key: "thumbnail_url", label: "Vignette (URL)", type: "text" },
          { key: "storage_key", label: "Clé de stockage", type: "text" },
          { key: "caption", label: "Légende", type: "textarea" },
          { key: "captured_at", label: "Capturé le", type: "datetime" },
          { key: "location", label: "Lieu", type: "text" },
          { key: "event_id", label: "ID événement", type: "text" },
          { key: "partner_id", label: "ID partenaire", type: "text" },
          { key: "creator_id", label: "ID créateur", type: "text" },
          { key: "photographer_id", label: "ID photographe", type: "text" },
          { key: "videographer_id", label: "ID vidéaste", type: "text" },
          { key: "people_ids", label: "IDs personnes", type: "csv" },
          { key: "ambassador_ids", label: "IDs ambassadeurs", type: "csv" },
          { key: "member_ids", label: "IDs membres", type: "csv" },
          { key: "challenge_id", label: "ID challenge", type: "text" },
          { key: "tags", label: "Étiquettes", type: "csv" },
          { key: "consent_status", label: "Statut de consentement", type: "select", options: ["pending", "granted", "revoked", "not_required"] },
          { key: "publication_status", label: "Statut de publication", type: "select", options: ["draft", "published", "archived"] },
          { key: "featured_status", label: "En vedette", type: "bool" },
          { key: "is_demo", label: "Contenu démo", type: "bool" },
        ]}
      />
    </div>
  );
};

export const AdminCollaborators = () => (
  <AdminResource
    resource="collaborators"
    title="Collaborateurs"
    description="Modèle générique : performer, robot, entertainment, studio, activation_partner, production_partner. Aucun fournisseur codé en dur."
    columns={[
      { key: "name", label: "Nom" },
      { key: "collaborator_type", label: "Type" },
      { key: "status", label: "Statut" },
      { key: "is_demo", label: "Démo" },
    ]}
    fields={[
      { key: "name", label: "Nom", type: "text" },
      {
        key: "collaborator_type",
        label: "Type",
        type: "select",
        options: ["performer", "robot", "entertainment", "studio", "activation_partner", "production_partner"],
      },
      { key: "description", label: "Description", type: "textarea" },
      { key: "contact_email", label: "Courriel de contact", type: "text" },
      { key: "website", label: "Site web", type: "text" },
      { key: "is_demo", label: "Contenu démo", type: "bool" },
    ]}
  />
);
